import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { ActuatorStateI, AppliancesStateI, DetectorStateI, DinDeviceI } from '../redux/types/app';
import { DeviceI } from '../redux/types/controllers';
import { addPublicURL } from './AddPublicURL';
import { formatEntityId, formatOptionalEntityId } from './FormatEntityId';

export const FLOORPLAN_EXPORT_TARGET_ID = 'floorplan-export-target';
export const DISTRIBUTION_BOARD_EXPORT_TARGET_ID = 'distribution-board-export-target';

const PDF_FONT = 'OpenSans';

const formatPrice = (price?: number): string => (price ? `${price.toFixed(2)} \u20AC` : '-');

const sumPrices = (prices: (number | undefined)[]): number =>
    prices.reduce((total: number, price) => total + (price || 0), 0);

export interface ExportToPdfLabelsI {
    title: string;
    controllersHeading: string;
    actuatorsHeading: string;
    detectorsHeading: string;
    othersHeading: string;
    appliancesHeading: string;
    id: string;
    type: string;
    name: string;
    price: string;
    description: string;
    total: string;
    grandTotal: string;
    technology: string;
    date: string;
    noItems: string;
    floorplanHeading: string;
    distributionBoardHeading: string;
    dinDevicesHeading: string;
    dinDevicesPriceNote: string;
}

const captureElementImage = async (targetId: string): Promise<{ dataUrl: string; width: number; height: number } | null> => {
    const element = document.getElementById(targetId);
    if (!element) {
        return null;
    }

    const canvas = await html2canvas(element, { backgroundColor: '#ffffff', useCORS: true, scale: 2 });
    return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
};

// jsPDF can't rasterize SVGs directly, so the logo is drawn onto a canvas first.
const loadImageAsPngDataUrl = (url: string, targetWidth: number): Promise<{ dataUrl: string; width: number; height: number } | null> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const scale = targetWidth / (img.naturalWidth || targetWidth);
            const canvas = document.createElement('canvas');
            canvas.width = targetWidth;
            canvas.height = (img.naturalHeight || targetWidth) * scale;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                resolve(null);
                return;
            }

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve({ dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height });
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunkSize)));
    }

    return btoa(binary);
};

// Embeds Open Sans (which supports Czech diacritics) so autoTable/text render correctly, unlike jsPDF's built-in fonts.
const registerUnicodeFont = async (doc: jsPDF): Promise<void> => {
    try {
        const [regularBuffer, boldBuffer] = await Promise.all([
            fetch(addPublicURL('/assets/fonts/OpenSans-Regular.ttf')).then((response) => response.arrayBuffer()),
            fetch(addPublicURL('/assets/fonts/OpenSans-Bold.ttf')).then((response) => response.arrayBuffer())
        ]);

        doc.addFileToVFS('OpenSans-Regular.ttf', arrayBufferToBase64(regularBuffer));
        doc.addFont('OpenSans-Regular.ttf', PDF_FONT, 'normal');
        doc.addFileToVFS('OpenSans-Bold.ttf', arrayBufferToBase64(boldBuffer));
        doc.addFont('OpenSans-Bold.ttf', PDF_FONT, 'bold');
        doc.setFont(PDF_FONT, 'normal');
    } catch {
        // Fall back to jsPDF's default font if the custom font can't be loaded.
    }
};

const ID_COLUMN_WIDTH = 22;
const PRICE_COLUMN_WIDTH = 32;

export const exportConfigurationToPdf = async (
    controllers: DeviceI[],
    actuators: ActuatorStateI[],
    detectors: DetectorStateI[],
    otherDevices: DinDeviceI[],
    appliances: AppliancesStateI[],
    technology: string,
    labels: ExportToPdfLabelsI
): Promise<void> => {
    const doc = new jsPDF();
    await registerUnicodeFont(doc);

    const pageWidth = doc.internal.pageSize.getWidth();
    const tableStyles = { fontSize: 9, font: PDF_FONT };
    const headStyles = { fillColor: [0, 145, 234] as [number, number, number], font: PDF_FONT, fontStyle: 'bold' as const };
    const footStyles = { font: PDF_FONT, fontStyle: 'bold' as const };

    let cursorY = 14;

    const logo = await loadImageAsPngDataUrl(addPublicURL('/assets/icons/logo.svg'), 400);
    if (logo) {
        const logoWidth = 30;
        const logoHeight = (logo.height / logo.width) * logoWidth;
        doc.addImage(logo.dataUrl, 'PNG', pageWidth - 14 - logoWidth, 10, logoWidth, logoHeight);
    }

    doc.setFontSize(16);
    doc.text(labels.title, 14, cursorY);
    cursorY += 8;

    doc.setFontSize(10);
    const generatedDate = new Date().toLocaleDateString();
    doc.text(`${labels.technology}: ${technology}    ${labels.date}: ${generatedDate}`, 14, cursorY);
    cursorY += 8;

    doc.setFontSize(12);
    doc.text(labels.controllersHeading, 14, cursorY);
    cursorY += 4;

    if (controllers.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
        cursorY += 8;
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.type, labels.price]],
            body: controllers.map((controller) => [
                formatEntityId('controller', controller.id),
                `${controller.name}${controller.colorShortcut}`,
                formatPrice(controller.price)
            ]),
            foot: [['', labels.total, formatPrice(sumPrices(controllers.map((controller) => controller.price)))]],
            theme: 'grid',
            styles: tableStyles,
            headStyles,
            footStyles,
            columnStyles: { 0: { cellWidth: ID_COLUMN_WIDTH }, 2: { halign: 'right', cellWidth: PRICE_COLUMN_WIDTH } }
        });
        cursorY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    doc.setFontSize(12);
    doc.text(labels.detectorsHeading, 14, cursorY);
    cursorY += 4;

    if (detectors.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
        cursorY += 8;
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.type, labels.price]],
            body: detectors.map((detector) => [
                formatOptionalEntityId('detector', detector.id),
                detector.name,
                formatPrice(detector.price)
            ]),
            foot: [['', labels.total, formatPrice(sumPrices(detectors.map((detector) => detector.price)))]],
            theme: 'grid',
            styles: tableStyles,
            headStyles,
            footStyles,
            columnStyles: { 0: { cellWidth: ID_COLUMN_WIDTH }, 2: { halign: 'right', cellWidth: PRICE_COLUMN_WIDTH } }
        });
        cursorY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    doc.setFontSize(12);
    doc.text(labels.actuatorsHeading, 14, cursorY);
    cursorY += 4;

    if (actuators.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
        cursorY += 8;
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.type, labels.name, labels.price]],
            body: actuators.map((actuator) => [
                formatOptionalEntityId('actuator', actuator.id),
                actuator.type,
                actuator.name,
                formatPrice(actuator.price)
            ]),
            foot: [['', '', labels.total, formatPrice(sumPrices(actuators.map((actuator) => actuator.price)))]],
            theme: 'grid',
            styles: tableStyles,
            headStyles,
            footStyles,
            columnStyles: { 0: { cellWidth: ID_COLUMN_WIDTH }, 3: { halign: 'right', cellWidth: PRICE_COLUMN_WIDTH } }
        });
        cursorY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    doc.setFontSize(12);
    doc.text(labels.othersHeading, 14, cursorY);
    cursorY += 4;

    if (otherDevices.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
        cursorY += 8;
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.type, labels.description, labels.price]],
            body: otherDevices.map((device) => [
                formatOptionalEntityId('other', device.id),
                device.type,
                device.name,
                formatPrice(device.price)
            ]),
            foot: [['', '', labels.total, formatPrice(sumPrices(otherDevices.map((device) => device.price)))]],
            theme: 'grid',
            styles: tableStyles,
            headStyles,
            footStyles,
            columnStyles: { 0: { cellWidth: ID_COLUMN_WIDTH }, 3: { halign: 'right', cellWidth: PRICE_COLUMN_WIDTH } }
        });
        cursorY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    doc.setFontSize(12);
    doc.text(labels.appliancesHeading, 14, cursorY);
    cursorY += 4;

    if (appliances.length === 0) {
        cursorY += 6;
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY);
        cursorY += 8;
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.name]],
            body: appliances.map((appliance) => [
                formatEntityId('appliance', appliance.id),
                appliance.label
            ]),
            theme: 'grid',
            styles: tableStyles,
            headStyles: { fillColor: [150, 150, 150] as [number, number, number], font: PDF_FONT, fontStyle: 'bold' as const }
        });
        cursorY = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 10;
    }

    const grandTotal = sumPrices([
        ...controllers.map((controller) => controller.price),
        ...detectors.map((detector) => detector.price),
        ...actuators.map((actuator) => actuator.price),
        ...otherDevices.map((device) => device.price)
    ]);
    doc.setFontSize(12);
    doc.setFont(PDF_FONT, 'bold');
    doc.text(`${labels.grandTotal}: ${formatPrice(grandTotal)}`, pageWidth - 14, cursorY, { align: 'right' });
    doc.setFont(PDF_FONT, 'normal');

    const floorplanImage = await captureElementImage(FLOORPLAN_EXPORT_TARGET_ID);
    if (floorplanImage) {
        doc.addPage();
        doc.setFontSize(16);
        doc.text(labels.floorplanHeading, 14, 14);

        const pageHeight = doc.internal.pageSize.getHeight();
        const availableWidth = pageWidth - 28;
        const availableHeight = pageHeight - 28;
        const scale = Math.min(availableWidth / floorplanImage.width, availableHeight / floorplanImage.height);
        const imageWidth = floorplanImage.width * scale;
        const imageHeight = floorplanImage.height * scale;

        doc.addImage(floorplanImage.dataUrl, 'PNG', 14, 24, imageWidth, imageHeight);
    }

    const distributionBoardImage = await captureElementImage(DISTRIBUTION_BOARD_EXPORT_TARGET_ID);
    if (!distributionBoardImage) {
        throw new Error('Distribution board export target is missing.');
    }

    doc.addPage();
    doc.setFontSize(16);
    doc.text(labels.distributionBoardHeading, 14, 14);
    const boardScale = Math.min(
        (pageWidth - 28) / distributionBoardImage.width,
        100 / distributionBoardImage.height
    );
    const boardWidth = distributionBoardImage.width * boardScale;
    const boardHeight = distributionBoardImage.height * boardScale;
    doc.addImage(distributionBoardImage.dataUrl, 'PNG', 14, 24, boardWidth, boardHeight, undefined, 'FAST');
    cursorY = 24 + boardHeight + 10;

    doc.setFontSize(12);
    doc.text(labels.dinDevicesHeading, 14, cursorY);
    doc.setFontSize(9);
    const priceNote = doc.splitTextToSize(labels.dinDevicesPriceNote, pageWidth - 28);
    doc.text(priceNote, 14, cursorY + 6);
    cursorY += 6 + priceNote.length * 4;

    if (actuators.length === 0 && otherDevices.length === 0) {
        doc.setFontSize(10);
        doc.text(labels.noItems, 14, cursorY + 6);
    } else {
        autoTable(doc, {
            startY: cursorY + 2,
            head: [[labels.id, labels.type, labels.description, labels.price]],
            body: [
                ...actuators.map((actuator) => [
                    formatOptionalEntityId('actuator', actuator.id),
                    actuator.type,
                    actuator.name,
                    formatPrice(actuator.price)
                ]),
                ...otherDevices.map((device) => [
                    formatOptionalEntityId('other', device.id),
                    device.type,
                    device.name,
                    formatPrice(device.price)
                ])
            ],
            theme: 'grid',
            styles: tableStyles,
            headStyles,
            columnStyles: { 0: { cellWidth: ID_COLUMN_WIDTH }, 3: { halign: 'right', cellWidth: PRICE_COLUMN_WIDTH } }
        });
    }

    doc.save('configuration.pdf');
};
